import moment from 'moment';
import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xsolla/ui-core';
import { useModal } from '@xsolla/ui-modal';

import { UserSocialProfile } from '../../../../api/users/types';
import RemoveSocialProfileModal from './RemoveSocialProfileModal';

type Props = {
  projectId: string;
  userId: string;
  userSocialProfile: UserSocialProfile[] | [];
  onSocialProfileRemoved: (providerName: string) => void;
};

const TableWrapper = styled.div`
  padding-top: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeaderCell = styled.th`
  border: 0px;
  padding-right: 10px;
  text-align: left !important;
  text-transform: capitalize;
`;

const TableDataCell = styled.td`
  border: 0px;
  padding-right: 10px;
  text-align: left;
`;

const fieldsToDisplay = ['provider_name', 'social_id', 'created_at'] as const;

export const SocialProfileTable = ({
  projectId,
  userId,
  userSocialProfile,
  onSocialProfileRemoved
}: Props): JSX.Element => {
  const intl = useIntl();
  const providerToRemove = useRef('');
  const [fetchingProvider, setFetchingProvider] = useState<string | null>(null);

  const [onOpenRemoveModal, onCloseRemoveModal] = useModal(
    () => (
      <RemoveSocialProfileModal
        projectId={projectId}
        userId={userId}
        providerName={providerToRemove.current}
        onClose={onCloseRemoveModal}
        onRemoved={onSocialProfileRemoved}
        onFetchingChange={(fetching) =>
          setFetchingProvider(fetching ? providerToRemove.current : null)
        }
      />
    ),
    [projectId, userId, onSocialProfileRemoved]
  );

  const handleRemoveClick = (providerName: string) => {
    providerToRemove.current = providerName;
    onOpenRemoveModal();
  };

  return (
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            {fieldsToDisplay.map((field) => (
              <TableHeaderCell key={field}>
                {intl.messages[`users.settings.general-info.social-profile.${field}`]}
              </TableHeaderCell>
            ))}
            <TableHeaderCell />
          </tr>
        </thead>
        <tbody>
          {userSocialProfile.map((item) => (
            <tr key={item.provider_name}>
              {fieldsToDisplay.map((field) => (
                <TableDataCell key={field}>
                  {field === 'created_at' ? moment(item[field]).format('L LT') : item[field]}
                </TableDataCell>
              ))}
              <TableDataCell>
                <Button
                  type="button"
                  look="link"
                  palette="danger"
                  size="md"
                  onClick={() => handleRemoveClick(item.provider_name)}
                  fetching={fetchingProvider === item.provider_name}
                  disabled={!!fetchingProvider}
                  data-test-id={`user-remove-social-profile-${item.provider_name}`}>
                  {intl.messages['users.settings.general-info.social-profile.remove']}
                </Button>
              </TableDataCell>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableWrapper>
  );
};
